import { createSelector } from 'reselect';

import { getTextEditing, getPaging, mountSelectorAt } from 'utils/selectors';
import * as goalSelectors from 'selectors/goals';

const mount = mountSelectorAt('chat');

export const getChatTextEditing = mount((localState, key) => getTextEditing(localState.textEditing[key]));

export const getChatPaging = mount((localState, key, options) => getPaging(localState.paging[key], options));

export const getChatQueueRejection = mount(localState => localState.queueRejection);

export const getChatState = mount(localState => {
  const chatState = localState.state;
  let stringValue;

  if(!chatState.isOnline) {
    stringValue = 'OFFLINE';
  }
  else if(chatState.isActive) {
    if(!chatState.didOpenChat) {
      stringValue = 'ACTIVE_NOT_STARTED';
    }
    else {
      stringValue = 'ACTIVE_STARTED';
    }
  }
  else if(chatState.waitingToCancelEnqueue) {
    stringValue = 'WAITING_TO_CANCEL_ENQUEUE';
  }
  else if(chatState.isQueued) {
    stringValue = 'QUEUED';
  }
  else if(chatState.waitingToEnqueue) {
    stringValue = 'WAITING_TO_ENQUEUE';
  }
  else {
    stringValue = 'ONLINE';
  }

  return {
    ...chatState,
    stringValue
  };
});

export const getChatWritingIndicator = mount(localState => localState.chatWritingIndicator);

export const getChatDocumentsById = mount(localState => localState.chatDocuments.byId);

export const getChatMessagesById = mount(localState => localState.chatMessages.byId);

export const getChatWebsitesById = mount(localState => localState.chatWebsites.byId);

export const getChatItemsNormalized = mount(localState => localState.chatItems.list);

export const getChatQuestionsById = mount(localState => localState.chatQuestions.byId);

export const getChatQuestionnairesById = mount(localState => localState.chatQuestionnaires.byId);


export const getChatMessagesBuffer = mount(localState => {
  const chatMessagesIds = localState.chatItems.chatMessagesBuffer;

  return chatMessagesIds.map(id => localState.chatMessages.byId[id]);
});

export const getChatLastMessageId = mount(localState => localState.chatItems.lastChatMessageId);

export const getChatActiveState = mount(localState => localState.activeState);

export const getChatQuestionnaire = mount((localState, id) => {
  const chatQuestionnaire = localState.chatQuestionnaires.byId[id];

  if(!chatQuestionnaire) {
    return null;
  }

  const questions = chatQuestionnaire.questions.map(id => localState.chatQuestions.byId[id]);

  return {
    ...chatQuestionnaire,
    questions
  };
});

export const getChatFeedbackSubmission = mount(localState => localState.feedbackSubmission);

export const getChatFeedbackIsFilled = mount((localState, rootState) => {
  const chatQuestionnaire = getChatQuestionnaire(rootState, 'eos');

  if(!chatQuestionnaire) {
    return false;
  }

  const feedbackSubmission = getChatFeedbackSubmission(rootState);

  return chatQuestionnaire.questions.length === feedbackSubmission.answeredChatQuestionsIds.length;
});

const getChatResourcesIdsForPage = mount((localState, props) => {
  const { page } = props;

  const chatResourcesIds = localState.chatResources.byPage[page];

  if(!chatResourcesIds) {
    return [];
  }

  if(!props.limit) {
    return chatResourcesIds;
  }

  return chatResourcesIds.slice(0, Math.min(chatResourcesIds.length, props.limit));
});

const getChatResourcesById = mount(localState => localState.chatResources.byId);

export const getChatResources = createSelector(
  [getChatResourcesIdsForPage, getChatResourcesById],
  (chatResourcesIdsForPage, chatResourcesById) => {
    return chatResourcesIdsForPage.map(id => {
      const chatResource = chatResourcesById[id];

      return chatResource;
    });
  }
);

export const getFirstChatScheduledSessionStartDate = mount(localState => localState.chatScheduledSessions[0] && localState.chatScheduledSessions[0].startDate);

export const getChatEmojis = mount((localState, key) => localState.emojis[key]);

export const getChatItems = createSelector(
  [getChatItemsNormalized, getChatMessagesById, getChatDocumentsById, getChatWebsitesById, goalSelectors.getGoalsById, getChatQuestionnairesById, getChatQuestionsById],
  (chatItemsNormalized, chatMessagesById, chatDocumentsById, chatWebsitesById, goalsById, chatQuestionnairesById, chatQuestionsById) => {

  return chatItemsNormalized.map(chatItemNormalized => {
    if(chatItemNormalized.chatMessageId !== undefined) {
      return { chatMessage: chatMessagesById[chatItemNormalized.chatMessageId] };
    }

    if(chatItemNormalized.chatDocumentId !== undefined) {
      return { chatDocument: chatDocumentsById[chatItemNormalized.chatDocumentId] };
    }

    if(chatItemNormalized.chatWebsiteId !== undefined) {
      return { chatWebsite: chatWebsitesById[chatItemNormalized.chatWebsiteId] };
    }

    if(chatItemNormalized.goalsIds !== undefined) {
      return { goals: chatItemNormalized.goalsIds.map(id => goalsById[id]) };
    }

    if(chatItemNormalized.chatQuestionnaireId !== undefined) {
      const questionnaire = chatQuestionnairesById[chatItemNormalized.chatQuestionnaireId];
      const questions = questionnaire.questions.map(id => chatQuestionsById[id]);

      return {
        chatQuestionnaire : {
          ...questionnaire,
          questions
        }
      };
    }
  });
});



/** WEBPACK FOOTER **
 ** ./src/members/state/chat/selectors.js
 **/